import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fxRevaluations } from '@/api/fxRevaluations';
import { orgs } from '@/api/catalogs';
import { accounts as accountsApi } from '@/api/documents';
import { useConfirm } from '@/components/ConfirmProvider';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import DatePopover from '@/components/DatePopover';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  revaluation_date: string;
  comment: string;
}

const today = new Date().toISOString().slice(0, 10);

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  revaluation_date: today,
  comment: '',
};

const fmt = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtCur = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

const KIND_LABEL: Record<string, string> = {
  INCOME: 'дохід',
  EXPENSE: 'витрата',
  NONE: '—',
};

export default function FxRevaluationEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX β: фільтр org dropdown по accounting.manual.manage permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('accounting.manual.manage');
  const accountsQ = useQuery({
    queryKey: ['accounts-all'],
    queryFn: () => accountsApi.list({ chart_code: 'HOZRASCHETNY', limit: 1000 }),
  });
  const docQuery = useQuery({
    queryKey: ['fx-revaluation', id],
    queryFn: () => fxRevaluations.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const numberPreview = useNumberPreview('doc.fx_revaluations', form.organization_id, isNew);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isNew) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : { ...f, organization_id: firstOrg });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        revaluation_date: d.revaluation_date.slice(0, 10),
        comment: d.comment ?? '',
      });
    }
  }, [isNew, docQuery.data, availableOrgs]);

  const accountByCode = useMemo(() => {
    const m = new Map<string, string>();
    accountsQ.data?.forEach((a) => m.set(a.id, a.code));
    return m;
  }, [accountsQ.data]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);
  const items = docQuery.data?.items ?? [];

  const create = useMutation({
    mutationFn: () => fxRevaluations.create({
      organization_id: form.organization_id,
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      revaluation_date: form.revaluation_date,
      comment: form.comment || null,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['fx-revaluations'] });
      navigate(`/fx-revaluations/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => fxRevaluations.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      revaluation_date: form.revaluation_date,
      comment: form.comment || null,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fx-revaluation', id] }),
    onError: (e) => setError(extractError(e)),
  });
  const recalc = useMutation({
    mutationFn: () => fxRevaluations.recalc(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fx-revaluation', id] }),
    onError: (e) => setError(extractError(e)),
  });
  // Збережений: зберегти поточні правки і одразу провести (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await fxRevaluations.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        revaluation_date: form.revaluation_date,
        comment: form.comment || null,
      });
      await fxRevaluations.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fx-revaluation', id] });
      qc.invalidateQueries({ queryKey: ['fx-revaluations'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => fxRevaluations.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fx-revaluation', id] });
      qc.invalidateQueries({ queryKey: ['fx-revaluations'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => fxRevaluations.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fx-revaluations'] });
      navigate('/fx-revaluations');
    },
  });

  // UX β: warning якщо новий документ а user не має accounting.manual.manage у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            padding: '1rem', background: '#fef3c7', borderRadius: 6,
            border: '1px solid #fbbf24', color: '#92400e',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати переоцінку валюти у жодній з ваших
            організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/fx-revaluations')}>Скасувати</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова переоцінка валюти' : `Переоцінка: ${form.document_number}`}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ проведено
            </span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <DocumentToolbar
            onBackToList={() => navigate('/fx-revaluations')}
            onSave={() => isNew ? create.mutate() : update.mutate()}
            saveDisabled={create.isPending || update.isPending}
            savePending={create.isPending || update.isPending}
            isPosted={isPosted}
            isNew={isNew}
            postDisabled={!isNew && items.length === 0}
            onPost={() => updateAndPost.mutate()}
            postPending={updateAndPost.isPending || update.isPending}
            postTitle="Зберігає поточні зміни та проводить документ"
            onUnpost={() => unpost.mutate()}
            unpostPending={unpost.isPending}
            onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }) }}
            deletePending={remove.isPending}
          />
          {!isNew && !isPosted && (
            <button
              onClick={() => recalc.mutate()}
              disabled={recalc.isPending}
              title="Розрахувати курсові різниці на дату переоцінки"
            >
              Перерахувати
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Організація</label>
          <select
            value={form.organization_id}
            onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            disabled={!isNew}
          >
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field">
          <label>№ документа</label>
          <input
            value={form.document_number}
            onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
            disabled={isPosted}
          />
        </div>
        <div className="field">
          <label>Дата документа</label>
          <DatePopover
            withTime
            value={form.document_date}
            onChange={(v) => setForm({ ...form, document_date: v })}
            disabled={isPosted}
          />
        </div>
        <div className="field">
          <label>Дата переоцінки</label>
          <DatePopover
            value={form.revaluation_date}
            onChange={(v) => setForm({ ...form, revaluation_date: v })}
            disabled={isPosted}
          />
        </div>
      </div>

      <div className="field">
        <label>Коментар</label>
        <input
          value={form.comment}
          onChange={(e) => setForm({ ...form, comment: e.target.value })}
          disabled={isPosted}
        />
      </div>

      {error && <div className="error">{error}</div>}

      {!isNew && items.length === 0 && (
        <p style={{ marginTop: '1rem', color: '#64748b', fontSize: '0.9rem' }}>
          Натисніть «Перерахувати», щоб система проаналізувала залишки за валютними
          рахунками на дату переоцінки і запропонувала курсові різниці.
        </p>
      )}

      {items.length > 0 && (
        <>
          <h3 style={{ marginTop: '1.5rem' }}>Курсові різниці</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                <th style={{ padding: '0.5rem', width: 40 }}>#</th>
                <th style={{ padding: '0.5rem', width: 80 }}>Рах.</th>
                <th style={{ padding: '0.5rem', width: 130, textAlign: 'right' }}>Залишок (вал.)</th>
                <th style={{ padding: '0.5rem', width: 100, textAlign: 'right' }}>Курс</th>
                <th style={{ padding: '0.5rem', width: 130, textAlign: 'right' }}>Стара UAH</th>
                <th style={{ padding: '0.5rem', width: 130, textAlign: 'right' }}>Нова UAH</th>
                <th style={{ padding: '0.5rem', width: 130, textAlign: 'right' }}>Різниця</th>
                <th style={{ padding: '0.5rem', width: 100 }}>Тип</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const diff = Number(it.fx_diff);
                const bg = it.entry_kind === 'INCOME' ? '#dcfce7'
                  : it.entry_kind === 'EXPENSE' ? '#fee2e2' : undefined;
                return (
                  <tr key={it.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '0.5rem' }}>{it.line_number}</td>
                    <td style={{ padding: '0.5rem', fontWeight: 600 }}>
                      {accountByCode.get(it.account_id) ?? '?'}
                    </td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmtCur(Number(it.currency_balance))}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmtCur(Number(it.new_rate))}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', color: '#64748b' }}>{fmt(Number(it.old_uah_balance))}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(Number(it.new_uah_balance))}</td>
                    <td style={{
                      padding: '0.5rem', textAlign: 'right', fontWeight: 600, background: bg,
                    }}>
                      {diff > 0 ? '+' : ''}{fmt(diff)}
                    </td>
                    <td style={{ padding: '0.5rem' }}>{KIND_LABEL[it.entry_kind] ?? it.entry_kind}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid #cbd5e1', fontWeight: 600 }}>
                <td colSpan={6} style={{ padding: '0.5rem', textAlign: 'right' }}>Усього:</td>
                <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                  <span style={{ color: '#15803d' }}>+{fmt(Number(docQuery.data?.total_income ?? 0))}</span>
                  {' / '}
                  <span style={{ color: '#b91c1c' }}>-{fmt(Number(docQuery.data?.total_expense ?? 0))}</span>
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </>
      )}

      {!isPosted && items.length > 0 && (
        <p style={{ marginTop: '1rem', color: '#64748b', fontSize: '0.85rem' }}>
          <strong>Як працюють проводки:</strong> для рядків з типом «дохід» формується
          {' '}<strong>Дт &lt;валютний рахунок&gt; — Кт 714</strong>, для «витрат» —
          {' '}<strong>Дт 945 — Кт &lt;валютний рахунок&gt;</strong>.
          Курс 714/945 — операційні курсові різниці. Сума береться з різниці між
          новою і старою гривневою оцінкою.
        </p>
      )}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
